import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Snackbar } from '@mui/material';
import {
  sendRecruiterMessage,
  sendRecruiterMessageWithAttachment,
  markRecruiterRead,
  refreshRecruiterConversation,
  searchCandidateMessages,
  loadOlderRecruiterMessages,
  loadRecruiterConversation,
  upsertChatMessage,
  setChatConversation,
  setChatPresence,
  applyReadReceipt,
} from '../../Redux/ActionCreators/chatAction';
import messengerSocket from '../../utils/messengerSocket';
import { handleRichEditorPaste } from './pasteSanitizer';
import './RecruiterChat.scss';

// ─── Avatar color map (A–Z) ───────────────────────────────────────────────────
const AVATAR_COLORS = {
  A: '#F75891', B: '#4A90D9', C: '#7B68EE', D: '#FF6B6B',
  E: '#00BCD4', F: '#F4A261', G: '#2D9E6B', H: '#E76F51',
  I: '#8338EC', J: '#3A86FF', K: '#06D6A0', L: '#E6A817',
  M: '#E63946', N: '#4361EE', O: '#F77F00', P: '#9C27B0',
  Q: '#0077B6', R: '#D62828', S: '#0288D1', T: '#558B2F',
  U: '#AD1457', V: '#1B6B45', W: '#6D6875', X: '#B5179E',
  Y: '#7209B7', Z: '#560BAD',
};

const getAvatarColor = (name) => {
  if (!name) return '#8a9199';
  const letter = name.trim().charAt(0).toUpperCase();
  return AVATAR_COLORS[letter] || '#8a9199';
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const MessengerIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="#0084ff" xmlns="http://www.w3.org/2000/svg" title="Sent via Messenger">
    <path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.84 1.345 5.38 3.465 7.075V22l3.338-1.836A10.87 10.87 0 0 0 12 20.518c5.523 0 10-4.145 10-9.259C22 6.145 17.523 2 12 2Zm1.006 12.47-2.547-2.716-4.97 2.716 5.467-5.8 2.61 2.716 4.906-2.716-5.466 5.8Z" />
  </svg>
);

const CheckboxIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" ry="3" />
  </svg>
);

const ResendIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-3.56" />
  </svg>
);

const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// ─── File type icon (same as DocumentCenter) ──────────────────────────────────
const resolveFileType = (name = '', mimeType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || mimeType.includes('pdf'))
    return { color: '#991b1b', bg: '#fee2e2', label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return { color: '#15803d', bg: '#dcfce7', label: 'XLS' };
  if (['doc', 'docx'].includes(ext) || mimeType.includes('word') || mimeType.includes('doc'))
    return { color: '#1e40af', bg: '#dbeafe', label: 'DOC' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image'))
    return { color: '#92400e', bg: '#fef3c7', label: 'IMG' };
  if (['txt', 'md', 'log'].includes(ext) || mimeType.includes('text') || mimeType.includes('plain'))
    return { color: '#374151', bg: '#f3f4f6', label: 'TXT' };
  if (['ppt', 'pptx'].includes(ext))
    return { color: '#c2410c', bg: '#ffedd5', label: 'PPT' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
    return { color: '#6d28d9', bg: '#ede9fe', label: 'ZIP' };
  return { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase().slice(0, 4) || 'FILE' };
};

const FileTypeIcon = ({ name, mimeType }) => {
  const { color, bg, label } = resolveFileType(name, mimeType);
  return (
    <svg width="36" height="42" viewBox="0 0 36 42" fill="none">
      <path d="M4 0h20l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={bg} />
      <path d="M24 0l12 12H28a4 4 0 0 1-4-4V0z" fill={color} fillOpacity="0.35" />
      <text x="18" y="30" textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily="Arial, sans-serif">{label}</text>
    </svg>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const handleDownload = async (url, fileName) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
};

const getDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const isHtmlEmpty = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim() === '';
};

const formatLastActive = (lastActiveAt, isOnline) => {
  if (isOnline) return 'Active now';
  if (!lastActiveAt) return 'Offline';
  const now = new Date();
  const lastActive = new Date(lastActiveAt);
  const diffMin = Math.floor((now - lastActive) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin <= 10) return `${diffMin} min ago`;
  const day = lastActive.getDate();
  const month = lastActive.toLocaleString('en-US', { month: 'short' });
  const year = lastActive.getFullYear();
  const time = lastActive.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${day} ${month} ${year}, ${time}`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const RecruiterChat = ({ jobCandidateId, candidateName, convLoading, isVisible = false, onMessageSent }) => {
  const dispatch = useDispatch();
  const conversation = useSelector((state) => state.chat.conversation);
  const messages = useSelector((state) => state.chat.messages);

  const [channel, setChannel] = useState('in_app');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const [headerSearch, setHeaderSearch] = useState('');
  const [searchMatchIds, setSearchMatchIds] = useState(new Set());
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);

  const lastMarkedReadId = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const headerSearchDebounceRef = useRef(null);
  const isFetchingOlderRef = useRef(false);
  const chatEditorRef = useRef(null);
  const messageBodyRef = useRef('');

  // ── Visibility ref (avoids stale closure in WS handler) ───────────
  const isVisibleRef = useRef(false);
  isVisibleRef.current = isVisible;

  // ── WebSocket refs ─────────────────────────────────────────────────
  const handleWsEventRef = useRef(null);

  useEffect(() => {
    setMessageBody('');
    messageBodyRef.current = '';
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';
    setPendingFile(null);
    setHeaderSearch('');
    setSearchMatchIds(new Set());
    setHasMoreMessages(true);
    setLoadingOlder(false);
    clearTimeout(headerSearchDebounceRef.current);
    lastMarkedReadId.current = null;
    isFetchingOlderRef.current = false;
    setChannel('in_app');
    try {
      const stored = localStorage.getItem(`gappeo_failed_rc_${jobCandidateId}`);
      setFailedMessage(stored ? JSON.parse(stored) : null);
    } catch {
      setFailedMessage(null);
    }
  }, [jobCandidateId]);

  useEffect(() => () => clearTimeout(headerSearchDebounceRef.current), []);

  // Scroll to first match in the current message list when results arrive
  useEffect(() => {
    if (!searchMatchIds.size) return;
    const firstMatch = messages.find((m) => searchMatchIds.has(m.id));
    if (firstMatch) {
      document.getElementById(`rc-msg-${firstMatch.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatchIds]);

  const handleHeaderSearch = (e) => {
    const val = e.target.value;
    setHeaderSearch(val);
    clearTimeout(headerSearchDebounceRef.current);
    if (!val.trim()) {
      setSearchMatchIds(new Set());
      return;
    }
    headerSearchDebounceRef.current = setTimeout(async () => {
      const result = await dispatch(searchCandidateMessages(jobCandidateId, val.trim()));
      setSearchMatchIds(new Set((result?.messages || []).map((m) => m.id)));
    }, 400);
  };

  const clearHeaderSearch = () => {
    setHeaderSearch('');
    setSearchMatchIds(new Set());
    clearTimeout(headerSearchDebounceRef.current);
  };

  // column-reverse: scrollTop starts at max (visual bottom). Scroll to bottom = scroll to max.
  // Only auto-scroll when user is already near the bottom.
  useEffect(() => {
    if (!messages.length) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop >= scrollHeight - clientHeight - 50;
    if (isNearBottom) {
      container.scrollTo({ top: scrollHeight - clientHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // Oldest message is last in the desc-ordered array; use it as the cursor for older pages
  const fetchOlderMessages = async () => {
    if (!hasMoreMessages || isFetchingOlderRef.current || !messages.length) return;
    isFetchingOlderRef.current = true;
    setLoadingOlder(true);
    const oldestMsg = messages[messages.length - 1];
    const result = await dispatch(loadOlderRecruiterMessages(jobCandidateId, oldestMsg.id));
    if (result?.hasMore === false) setHasMoreMessages(false);
    setLoadingOlder(false);
    isFetchingOlderRef.current = false;
  };

  // column-reverse: browser anchors scrollTop at max (visual bottom).
  // Scrolling UP toward older messages decreases scrollTop toward 0.
  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container || isFetchingOlderRef.current) return;
    if (container.scrollTop <= 50) fetchOlderMessages();
  };

  // desc order: index 0 = newest; mark newest candidate message as read only when visible
  useEffect(() => {
    if (!isVisibleRef.current || !messages.length || !conversation?.conversation_id) return;
    const newest = messages[0];
    if (newest?.sender_type === 'candidate' && newest.id !== lastMarkedReadId.current) {
      lastMarkedReadId.current = newest.id;
      dispatch(markRecruiterRead(jobCandidateId, newest.id)).then(() => {
        dispatch(refreshRecruiterConversation(jobCandidateId));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (conversation && !conversation.messenger_available && channel === 'messenger') {
      setChannel('in_app');
    }
  }, [conversation, channel]);

  // ── Load conversation + messages whenever jobCandidateId changes ───
  useEffect(() => {
    if (!jobCandidateId) return;
    dispatch(loadRecruiterConversation(jobCandidateId));
  }, [jobCandidateId, dispatch]);

  // ── Mark read when recruiter opens the panel with existing unread messages ──
  useEffect(() => {
    if (!isVisible || !messages.length || !conversation?.conversation_id) return;
    const newest = messages[0];
    if (newest?.sender_type === 'candidate' && newest.id !== lastMarkedReadId.current) {
      lastMarkedReadId.current = newest.id;
      dispatch(markRecruiterRead(jobCandidateId, newest.id)).then(() => {
        dispatch(refreshRecruiterConversation(jobCandidateId));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // ── WebSocket: event handler (ref so it always sees fresh state) ───
  handleWsEventRef.current = (data) => {
    if (data.type === 'your_id') return;

    if (data.type === 'group_members') {
      if (conversation?.conversation_id &&
          data.group === `candidate-conversation-${conversation.conversation_id}`) {
        dispatch(setChatPresence(!!data.recruiter_online, !!data.candidate_online));
      }
      return;
    }

    if (data.type === 'conversation_inbox_updated' && data.item) {
      // Inbox updates arrive for every conversation; merging another candidate's item
      // would swap in their conversation_id and let their messages into this chat
      if (String(data.item.job_candidate_id) === String(jobCandidateId)) {
        dispatch(setChatConversation({ ...conversation, ...data.item }));
      }
      return;
    }

    if (data.type === 'conversation_message' && data.message) {
      if (String(data.conversation_id) === String(conversation?.conversation_id)) {
        dispatch(upsertChatMessage(data.message));
        if (isVisibleRef.current && data.message.sender_type === 'candidate') {
          dispatch(markRecruiterRead(jobCandidateId, data.message.id)).then(() => {
            dispatch(refreshRecruiterConversation(jobCandidateId));
          });
        }
      }
      return;
    }

    if (data.type === 'message_read') {
      if (String(data.conversation_id) === String(conversation?.conversation_id)) {
        if (data.participant_type === 'candidate') {
          dispatch(applyReadReceipt('recruiter', data.last_read_message_id));
        }
      }
    }
  };

  // ── WebSocket: listen on the shared app socket (opened in mainLayout) ─
  useEffect(() => {
    const offMessage = messengerSocket.addListener('message', (data) => handleWsEventRef.current?.(data));
    return () => offMessage();
  }, []);

  // ── WebSocket: join the conversation group when the conversation loads ─
  useEffect(() => {
    const convId = conversation?.conversation_id;
    if (!convId) return;
    const group = `candidate-conversation-${convId}`;
    messengerSocket.joinGroup(group);
    return () => messengerSocket.leaveGroup(group);
  }, [conversation?.conversation_id]);

  const handleSend = async () => {
    const htmlBody = messageBodyRef.current;
    if ((isHtmlEmpty(htmlBody) && !pendingFile) || sending || failedMessage) return;
    setSending(true);
    let sentOk = false;

    messageBodyRef.current = '';
    setMessageBody('');
    if (chatEditorRef.current) chatEditorRef.current.innerHTML = '';

    if (pendingFile) {
      const fileToSend = pendingFile;
      setPendingFile(null);
      const result = await dispatch(sendRecruiterMessageWithAttachment(jobCandidateId, fileToSend.file, isHtmlEmpty(htmlBody) ? undefined : htmlBody));
      if (!result?.success) {
        setSnackMessage('Failed to send attachment. Please try again.');
        setSnackOpen(true);
      } else {
        sentOk = true;
      }
    } else {
      const result = await dispatch(sendRecruiterMessage(jobCandidateId, htmlBody, channel));
      if (!result?.success) {
        const failed = { text: htmlBody, channel, timestamp: new Date().toISOString() };
        setFailedMessage(failed);
        localStorage.setItem(`gappeo_failed_rc_${jobCandidateId}`, JSON.stringify(failed));
      } else {
        sentOk = true;
      }
    }

    setSending(false);
    dispatch(refreshRecruiterConversation(jobCandidateId));
    if (sentOk) onMessageSent?.();
  };

  const handleResend = async () => {
    if (!failedMessage || sending) return;
    const { text, channel: ch } = failedMessage;
    setFailedMessage(null);
    localStorage.removeItem(`gappeo_failed_rc_${jobCandidateId}`);
    setSending(true);
    const result = await dispatch(sendRecruiterMessage(jobCandidateId, text, ch));
    setSending(false);
    if (!result?.success) {
      const failed = { text, channel: ch, timestamp: new Date().toISOString() };
      setFailedMessage(failed);
      localStorage.setItem(`gappeo_failed_rc_${jobCandidateId}`, JSON.stringify(failed));
    } else {
      dispatch(refreshRecruiterConversation(jobCandidateId));
    }
  };

  const handleDeleteFailed = () => {
    setFailedMessage(null);
    localStorage.removeItem(`gappeo_failed_rc_${jobCandidateId}`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile({ file, name: file.name, size: file.size });
    e.target.value = '';
  };

  const clearFile = () => setPendingFile(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Keep text formatting (bold/italic/underline, lists, links, line breaks, text color)
  // but drop background/highlight colors and editor-specific junk on paste.
  const handleEditorPaste = (e) => {
    handleRichEditorPaste(e, chatEditorRef.current, (html) => {
      messageBodyRef.current = html;
      setMessageBody(html);
    });
  };
  const candidateOnline = conversation?.candidate_online;

  const messengerAvailable = !!conversation?.messenger_available;
  const displayName = candidateName || conversation?.candidate_name || 'Candidate';
  const jobTitle = conversation?.job_title || conversation?.job?.title;
  const companyName = conversation?.job_company_name
  const jobId = conversation?.job_id || conversation?.job?.id;
  const headerInitials = getInitials(displayName);
  const headerColor = getAvatarColor(displayName);

  const handleViewProfile = () => {
    if (jobId && jobCandidateId) window.open(`/jobs/${jobId}/candidates/${jobCandidateId}`, '_blank');
  };

  // Build grouped items for column-reverse (desc order: newest first).
  // Date separator goes AFTER the last message of each day in DOM order so it
  // appears ABOVE that day's group in the reversed visual layout.
  const groupedItems = [];
  let currentDayStr = null;
  let currentDayCreatedAt = null;
  messages.forEach((msg) => {
    const msgDayStr = msg.created_at ? new Date(msg.created_at).toDateString() : '';
    if (msgDayStr !== currentDayStr) {
      if (currentDayStr !== null) {
        groupedItems.push({ type: 'date', label: getDateLabel(currentDayCreatedAt) });
      }
      currentDayStr = msgDayStr;
      currentDayCreatedAt = msg.created_at;
    }
    groupedItems.push({ type: 'msg', msg });
  });
  if (currentDayStr !== null) {
    groupedItems.push({ type: 'date', label: getDateLabel(currentDayCreatedAt) });
  }

  if (convLoading) {
    return (
      <div className="rc-loading rc-loading--full">
        <div className="spinner-border" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rc-window rc-window--embedded">
        <div className="rc-header">
          <div className="rc-avatar" style={{ background: headerColor }}>{headerInitials}</div>
          <div className="rc-header-info">
            <div className="rc-name-row">
              <span className="rc-name">{displayName}</span>
              {jobId && jobCandidateId && (
                <button
                  className="rc-profile-link"
                  onClick={handleViewProfile}
                  title="View candidate profile"
                >
                  <ExternalLinkIcon />&nbsp;&nbsp;
                  <div className="rc-status">
                    <span className={`rc-dot${candidateOnline ? ' online' : ''}`} />
                    {formatLastActive(conversation?.candidate_last_active_at, candidateOnline)}
                  </div>
                </button>
              )}
            </div>
            {jobTitle && (
              <div className="rc-job-title">
                <img src='/images/bag-icon.svg' alt='' width={12} height={12} />&nbsp;{jobTitle}&nbsp;/&nbsp; 
                <img src='/images/company-icon.svg' alt='' width={12} height={12} />
                &nbsp;{companyName} 
              </div>
            )}
          </div>
          {/* <div className="rc-header-search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search…"
              value={headerSearch}
              onChange={handleHeaderSearch}
            />
            {headerSearch && (
              <button className="rc-header-search-clear" onClick={clearHeaderSearch} aria-label="Clear search">
                ×
              </button>
            )}
          </div> */}
        </div>


        <div className="rc-messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {failedMessage && (
            <div className="rc-message-row out rc-message-row--failed">
              <div className="rc-message out">
                <div className="rc-bubble rc-bubble--failed"><span dangerouslySetInnerHTML={{ __html: failedMessage.text }} /></div>
                <div className="rc-msg-meta rc-failed-meta">
                  <span className="rc-failed-label">Failed to send</span>
                  <button className="rc-failed-btn rc-failed-btn--resend" onClick={handleResend} disabled={sending} title="Resend message">
                    <ResendIcon /> Resend
                  </button>
                  <button className="rc-failed-btn rc-failed-btn--delete" onClick={handleDeleteFailed} title="Delete message">
                    <TrashIcon />
                  </button>
                </div>
              </div>
              <div className="rc-msg-avatar" style={{ background: headerColor }}>{headerInitials}</div>
            </div>
          )}
          {messages.length === 0 && !failedMessage && (
            <p className="rc-empty">No messages yet. Start the conversation.</p>
          )}

          {groupedItems.map((item, idx) => {
            if (item.type === 'date') {
              return (
                <div key={`date-${idx}`} className="rc-date-separator">
                  <span>{item.label}</span>
                </div>
              );
            }

            const { msg } = item;
            const isOut = msg.sender_type === 'recruiter';
            const msgInitials = getInitials(msg.sender_name);
            const msgColor = getAvatarColor(msg.sender_name);
            const senderTooltip = [msg.sender_name, msg.sender_email].filter(Boolean).join('\n');

            const hasAttachments = (msg.attachments || []).length > 0;
            const isHighlighted = searchMatchIds.has(msg.id);

            return (
              <div
                key={msg.id}
                id={`rc-msg-${msg.id}`}
                className={`rc-message-row ${isOut ? 'out' : 'in'}${isHighlighted ? ' rc-message-row--highlight' : ''}`}
              >
                {!isOut && (
                  <div
                    className="rc-msg-avatar"
                    style={{ background: msgColor }}
                    title={senderTooltip}
                  >
                    {msgInitials}
                  </div>
                )}

                <div className={`rc-message ${isOut ? 'out' : 'in'}`}>
                  <div className={`rc-bubble${hasAttachments ? ' rc-bubble--file-only' : ''}`}>
                    {!hasAttachments && (
                      <span dangerouslySetInnerHTML={{ __html: msg.body_text || '' }} />
                    )}
                    {(msg.attachments || []).map((att, i) => {
                      const fileName = att.file_name || att.name || 'File';
                      const fileSize = att.file_size ?? att.size ?? null;
                      const mimeType = att.mime_type || att.content_type || '';
                      return (
                        <div
                          key={i}
                          className="rc-file-card"
                          onClick={() => att.file_url && window.open(att.file_url, '_blank')}
                        >
                          <div className="rc-file-card__icon">
                            <FileTypeIcon name={fileName} mimeType={mimeType} />
                          </div>
                          <div className="rc-file-card__body">
                            <div className="rc-file-card__name">{fileName}</div>
                            {fileSize != null && (
                              <div className="rc-file-card__size">{formatFileSize(fileSize)}</div>
                            )}
                          </div>
                          <button
                            className="rc-file-card__dl"
                            title="Download"
                            onClick={(e) => { e.stopPropagation(); handleDownload(att.file_url, fileName); }}
                          >
                            <DownloadIcon />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="rc-msg-meta">
                    {msg.channel === 'messenger' && (
                      <span className="rc-meta-channel" title="Sent via Messenger">
                        <MessengerIcon />
                      </span>
                    )}
                    {!isOut && <span title={senderTooltip}>{msg.sender_name}</span>}
                    <span>{formatTime(msg.created_at)}</span>
                    {isOut && (
                      <span title={msg.is_read_by_other ? 'Read' : 'Delivered'}>
                        {msg.is_read_by_other ? '✓✓' : '✓'}
                      </span>
                    )}
                  </div>
                </div>

                {isOut && (
                  <div
                    className="rc-msg-avatar"
                    style={{ background: msgColor }}
                    title={senderTooltip}
                  >
                    {msgInitials}
                  </div>
                )}
              </div>
            );
          })}
          {loadingOlder && (
            <div className="rc-older-loading">
              <div className="spinner-border spinner-border-sm" role="status" />
            </div>
          )}
          {!hasMoreMessages && messages.length > 0 && (
            <div className="rc-conversation-start">Beginning of conversation</div>
          )}
        </div>

        {/* Messenger banner above composer */}
        {channel === 'messenger' && (
          <div className="rc-messenger-banner">
            <span className="rc-messenger-banner-icon"><MessengerIcon /></span>
            <span>Replying via Messenger</span>
          </div>
        )}
        <div className="rc-composer">
          {pendingFile && (
            <div className="rc-attach-preview">
              <div className="rc-attach-preview-icon">
                <FileTypeIcon name={pendingFile.name} mimeType={pendingFile.file?.type || ''} />
              </div>
              <div className="rc-attach-preview-body">
                <div className="rc-attach-preview-name">{pendingFile.name}</div>
                <div className="rc-attach-preview-meta">{formatFileSize(pendingFile.size)} · ready to send</div>
              </div>
              <button className="rc-attach-preview-clear" onClick={clearFile} aria-label="Remove file">×</button>
            </div>
          )}
          <div className="rc-composer-row">
            {channel === 'in_app' && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif"
                  onChange={handleFileChange}
                />
                <button
                  className="rc-attach-btn-trigger"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach file"
                  aria-label="Attach file"
                >
                  <AttachIcon />
                </button>
              </>
            )}
            {channel === 'messenger' ? (
              <textarea
                value={messageBody}
                onChange={(e) => {
                  messageBodyRef.current = e.target.value;
                  setMessageBody(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={failedMessage ? 'Resolve the failed message above first…' : pendingFile ? 'Remove file to type a message…' : 'Type a message…'}
                rows={1}
                disabled={!!pendingFile || !!failedMessage}
                className="rc-rich-input"
              />
            ) : (
              <div className="rc-rich-input-wrap">
                <div
                  ref={chatEditorRef}
                  contentEditable={!pendingFile && !failedMessage}
                  suppressContentEditableWarning
                  className="rc-rich-input"
                  data-placeholder={
                    failedMessage ? 'Resolve the failed message above first…'
                    : pendingFile ? 'Remove file to type a message…'
                    : 'Type a message…'
                  }
                  onInput={(e) => {
                    const html = e.currentTarget.innerHTML;
                    messageBodyRef.current = html;
                    setMessageBody(html);
                  }}
                  onPaste={handleEditorPaste}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
            {messengerAvailable && (
              <label className="rc-messenger-toggle" title={channel === 'messenger' ? 'Sending via Messenger' : 'Send via Messenger'}>
                <input
                  type="checkbox"
                  checked={channel === 'messenger'}
                  onChange={(e) => setChannel(e.target.checked ? 'messenger' : 'in_app')}
                />
                {channel === 'messenger' ? <MessengerIcon /> : <CheckboxIcon />}
                <span>Messenger</span>
              </label>
            )}
            <button
              className="rc-send-btn"
              onClick={handleSend}
              disabled={(isHtmlEmpty(messageBody) && !pendingFile) || sending || !!failedMessage}
              aria-label="Send"
              title='Send'
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="error" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RecruiterChat;
